/**
 * Orchestrates AI-first menu parsing with heuristic fallback.
 */
import {
  parseCsvMenu,
  parseHtmlMenu,
  parsePdfMenu,
  extractPdfText,
  fetchMenuFromUrl,
  buildMenuJson,
} from "./parseMenu";
import { buildRestaurantMenuImportJson } from "./menuImportFormat";
import {
  isAiParsingConfigured,
  aiParseImageFile,
  aiParseImageFiles,
  aiParsePdfFile,
  aiParseTextContent,
  aiParseHtmlContent,
} from "./aiParseMenu";
import { readEmbeddedParsedMenu } from "./parsedMenuEmbed";

const IMAGE_EXT = /\.(png|jpe?g|webp|gif|bmp)$/i;
const HTML_EXT = /\.(html?|htm)$/i;

export function detectFileKind(file) {
  const name = (file?.name || "").toLowerCase();
  const type = (file?.type || "").toLowerCase();
  if (name.endsWith(".pdf") || type.includes("pdf")) return "pdf";
  if (name.endsWith(".csv") || type.includes("csv")) return "csv";
  if (IMAGE_EXT.test(name) || type.startsWith("image/")) return "image";
  if (HTML_EXT.test(name) || type.includes("html")) return "html";
  return "unknown";
}

export { isAiParsingConfigured };

async function withAiFirst(aiFn, fallbackFn) {
  let aiError = null;
  try {
    const items = await aiFn();
    if (items?.length) return { items, parser: "ai" };
  } catch (err) {
    aiError = err;
  }

  if (fallbackFn) {
    const items = await fallbackFn();
    if (items?.length) {
      return {
        items,
        parser: "heuristic",
        warning: aiError
          ? `AI parse failed (${aiError.message}). Used local fallback.`
          : null,
      };
    }
  }

  if (aiError) throw aiError;
  return { items: [], parser: "none" };
}

export async function parseUploadedMenu({
  file,
  files,
  apiKey,
  useAi = true,
}) {
  const uploadList = (files?.length ? files : file ? [file] : []).filter(
    Boolean
  );
  if (!uploadList.length) {
    throw new Error("Please choose a menu file.");
  }

  if (uploadList.length > 1) {
    const kinds = uploadList.map((f) => detectFileKind(f));
    if (kinds.every((k) => k === "image")) {
      const aiEnabled = useAi && isAiParsingConfigured(apiKey);
      if (!aiEnabled) {
        throw new Error(
          "Image menus require AI vision parsing. Enable “Use AI parsing” and set REACT_APP_OPENAI_API_KEY or REACT_APP_DEEPSEEK_API_KEY in .env, then restart npm start."
        );
      }
      const items = await aiParseImageFiles(uploadList, apiKey);
      return { items, parser: "ai" };
    }
    throw new Error(
      "Multiple files are supported for images only (e.g. several menu photos). Use one PDF/CSV/HTML file at a time."
    );
  }

  const single = uploadList[0];
  const kind = detectFileKind(single);
  if (kind === "unknown") {
    throw new Error(
      "Unsupported file. Upload PDF, image (PNG/JPG/WEBP), CSV, or HTML."
    );
  }

  const aiEnabled = useAi && isAiParsingConfigured(apiKey);

  if (kind === "csv") {
    const text = await single.text();
    if (aiEnabled) {
      return withAiFirst(
        () => aiParseTextContent(text, `(csv: ${single.name})`, apiKey),
        () => parseCsvMenu(text)
      );
    }
    return { items: parseCsvMenu(text), parser: "heuristic" };
  }

  if (kind === "html") {
    const html = await single.text();
    if (aiEnabled) {
      return withAiFirst(
        () => aiParseHtmlContent(html, `(html: ${single.name})`, apiKey),
        () => parseHtmlMenu(html)
      );
    }
    return { items: parseHtmlMenu(html), parser: "heuristic" };
  }

  if (kind === "image") {
    if (!aiEnabled) {
      throw new Error(
        "Image menus require AI vision parsing. Enable “Use AI parsing” and set REACT_APP_OPENAI_API_KEY or REACT_APP_DEEPSEEK_API_KEY in .env, then restart npm start."
      );
    }
    const items = await aiParseImageFile(single, apiKey);
    return { items, parser: "ai" };
  }

  // PDF
  if (aiEnabled) {
    let extractedText = "";
    try {
      extractedText = await extractPdfText(single);
    } catch (_) {
      extractedText = "";
    }
    return withAiFirst(
      () => aiParsePdfFile(single, apiKey, extractedText),
      () => parsePdfMenu(single)
    );
  }

  return { items: await parsePdfMenu(single), parser: "heuristic" };
}

export async function parseLinkedMenu({ url, apiKey, useAi = true }) {
  const fetched = await fetchMenuFromUrl(url);
  const aiEnabled = useAi && isAiParsingConfigured(apiKey);
  const hint = `(url: ${url})`;

  if (fetched.spaItems?.length) {
    return { items: fetched.spaItems, parser: "heuristic-spa" };
  }

  if (fetched.kind === "html") {
    const embedded = readEmbeddedParsedMenu(fetched.text);
    if (embedded?.items?.length) {
      const strategies = embedded.meta?.strategies || [];
      return {
        items: embedded.items,
        parser: "menu-link-pipeline",
        pipelineMeta: embedded.meta,
        warning: strategies.length
          ? `Menu link pipeline: ${strategies.join(", ")}.`
          : "Menu fetched and parsed via server pipeline.",
      };
    }
  }

  if (fetched.kind === "csv") {
    if (aiEnabled) {
      return withAiFirst(
        () => aiParseTextContent(fetched.text, hint, apiKey),
        () => parseCsvMenu(fetched.text)
      );
    }
    return { items: parseCsvMenu(fetched.text), parser: "heuristic" };
  }

  if (fetched.kind === "pdf") {
    if (aiEnabled) {
      let extractedText = "";
      try {
        extractedText = await extractPdfText(fetched.file);
      } catch (_) {
        extractedText = "";
      }
      return withAiFirst(
        () => aiParsePdfFile(fetched.file, apiKey, extractedText),
        () => parsePdfMenu(fetched.file)
      );
    }
    return { items: await parsePdfMenu(fetched.file), parser: "heuristic" };
  }

  if (fetched.kind === "image") {
    if (!aiEnabled) {
      throw new Error(
        "Image menu URLs require AI vision parsing. Enable “Use AI parsing” and configure REACT_APP_OPENAI_API_KEY or REACT_APP_DEEPSEEK_API_KEY in .env, then restart npm start."
      );
    }
    const items = await aiParseImageFile(fetched.file, apiKey);
    return { items, parser: "ai" };
  }

  // html / webpage
  if (aiEnabled) {
    return withAiFirst(
      () => aiParseHtmlContent(fetched.text, hint, apiKey),
      () => parseHtmlMenu(fetched.text)
    );
  }
  return { items: parseHtmlMenu(fetched.text), parser: "heuristic" };
}

export function buildParsedMenuJson({
  items,
  source,
  sourceName,
  parser,
  provider,
  pipelineMeta,
}) {
  return buildRestaurantMenuImportJson({
    items,
    source,
    sourceName,
    parser,
    provider,
    pipelineMeta,
  });
}

/** @deprecated Use buildParsedMenuJson; kept for flat item preview if needed. */
export function buildParsedMenuJsonFlat(args) {
  const json = buildMenuJson({
    items: args.items,
    source: args.source,
    sourceName: args.sourceName,
  });
  return {
    ...json,
    parser: args.parser || "unknown",
    ...(args.provider ? { aiProvider: args.provider } : {}),
    ...(args.pipelineMeta ? { pipelineMeta: args.pipelineMeta } : {}),
  };
}
